import { useRef, useState } from 'react';
import { SpinReward } from '../types';
import { getRandomReward } from '../services/api'

interface UseRewardReelProps {
  items: SpinReward[];
  itemWidth: number;
  duration?: number;
  minRotations?: number;
  onSpinComplete?: (reward: SpinReward) => void;
}

interface UseRewardReelReturn {
  reelRef: React.RefObject<HTMLDivElement>;
  isSpinning: boolean;
  finalItemIndex: number | null;
  selectedReward: SpinReward | null;
  startSpinning: () => Promise<void>;
}

// Function to select a reward based on probabilities
const selectRewardByProbability = (rewards: SpinReward[]): SpinReward => {
  // Calculate total probability
  const totalProbability = rewards.reduce((sum, reward) => sum + reward.probability, 0);
  
  // Generate a random number between 0 and total probability
  const randomValue = Math.random() * totalProbability;
  
  // Find the reward that corresponds to the random value
  let cumulativeProbability = 0;
  for (const reward of rewards) {
    cumulativeProbability += reward.probability;
    if (randomValue <= cumulativeProbability) {
      return reward;
    }
  }
  
  // Fallback to the last reward (should never happen if probabilities sum to 100%)
  return rewards[rewards.length - 1];
};

export const useRewardReel = ({
  items,
  itemWidth,
  duration = 8000,
  minRotations = 10,
  onSpinComplete
}: UseRewardReelProps): UseRewardReelReturn => {
  const reelRef = useRef<HTMLDivElement>(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [finalItemIndex, setFinalItemIndex] = useState<number | null>(null);
  const [selectedReward, setSelectedReward] = useState<SpinReward | null>(null);

  const startSpinning = async (): Promise<void> => {
    // Reset any previous state
    setIsSpinning(true);
    setFinalItemIndex(null);
    
    // Determine the reward outcome before spinning
    // const reward = selectRewardByProbability(items);
    const reward = await getRandomReward();
    
    // Set the state for UI updates, but we'll use the local variable for calculations
    setSelectedReward(reward);
    
    // Find the index of the selected reward
    const targetIndex = items.findIndex(item => item.id === reward.id);
    
    // Animation parameters
    const start = performance.now();
    let lastTimestamp = start;
    
    // Use a manual position tracker instead of relying on scrollLeft
    // This ensures we have full control over the animation
    let position = 0;
    
    // Calculate the total width of all items
    const totalWidth = itemWidth * items.length;
    
    // Calculate target position for smooth landing
    // We need to be extremely precise to ensure we stop on the correct item
    const viewportCenter = reelRef.current?.offsetWidth ? reelRef.current.offsetWidth / 2 : 400;
    
    // Use a consistent number of rotations to ensure predictability
    const baseRotations = minRotations;
    
    // No random offset - we want exact positioning
    // Calculate the exact position where the target item should be centered
    const targetPosition = (baseRotations * totalWidth) + 
                          (targetIndex * itemWidth) - 
                          viewportCenter + 
                          (itemWidth / 2);
    
    // Variables for logging
    const logStartMessage = 'Starting spin animation';
    const logItemWidth = itemWidth;
    const logTargetPos = targetPosition;
    
    // Create a closure to capture the reward variable for the entire animation lifecycle
    const spin = (function(capturedReward) {
      return function(now: number): void {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        
        // Single smooth deceleration to the predetermined position
        // Using a cubic ease-out for natural deceleration
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        
        // Calculate position directly based on the target position and easing
        // For the first 80% of the animation, we use the easing function
        // For the last 20%, we ensure a precise landing by linearly approaching the target
        if (progress < 0.8) {
          position = targetPosition * easeOutCubic;
        } else {
          // Linear approach to target in final phase for precision
          const finalPhaseProgress = (progress - 0.8) / 0.2; // normalize to 0-1
          position = (targetPosition * easeOutCubic) * (1 - finalPhaseProgress) + (targetPosition * finalPhaseProgress);
        }
        
        // Apply the position to the scroll element
        // Use modulo to keep the scroll position within a reasonable range
        // This creates the illusion of infinite scrolling
        if (reelRef.current) {
          const totalWidth = itemWidth * items.length;
          const moduloPosition = position % totalWidth;
          
          // Set the scroll position directly
          reelRef.current.scrollLeft = moduloPosition;
          
          // Debug every 500ms
          if (now - lastTimestamp > 500) {
            lastTimestamp = now;
          }
        }

        if (progress < .99) {
          // Continue animation
          requestAnimationFrame(spin);
        } else if (reelRef.current) {
          // Ensure we're using the original target index from when we started spinning
          const targetIndex = items.findIndex(item => item.id === capturedReward.id);
          
          // Force exact positioning to ensure we're on the right item
          // Calculate the exact center position for the target item
          const totalWidth = itemWidth * items.length;
          const exactCenterPosition = (targetIndex * itemWidth) - viewportCenter + (itemWidth / 2);
          const finalPosition = (Math.floor(position / totalWidth) * totalWidth) + ((exactCenterPosition + totalWidth) % totalWidth);
          
          // Apply the exact position
          reelRef.current.scrollLeft = finalPosition % totalWidth;
          
          // Highlight the selected item
          setFinalItemIndex(targetIndex);
          
          // Complete the animation
          setIsSpinning(false);
          
          // Call the onSpinComplete callback if provided
          if (onSpinComplete) {
            onSpinComplete(capturedReward);
          }
          
          // Add a subtle bounce effect to emphasize the selected item
          const bounceItem = (): void => {
            // We need to wait a moment for the DOM to update with the highlighted item
            // Then find the visible item that's closest to the center
            setTimeout(() => {
              if (!reelRef.current) return;
              
              // Get the center position of the viewport
              const reelRect = reelRef.current.getBoundingClientRect();
              const centerX = reelRect.left + (reelRect.width / 2);
              
              // Use the captured reward to ensure consistency
              const rewardId = capturedReward.id;
              
              // Find all items with the selected ID
              const allMatchingItems = Array.from(
                document.querySelectorAll(`[data-reward-id="${rewardId}"]`)
              );
              
              // Find the one closest to the center of the viewport
              if (allMatchingItems.length > 0) {
                let closestItem = allMatchingItems[0];
                let closestDistance = Infinity;
                
                allMatchingItems.forEach(item => {
                  const rect = item.getBoundingClientRect();
                  const itemCenterX = rect.left + (rect.width / 2);
                  const distance = Math.abs(itemCenterX - centerX);
                  
                  if (distance < closestDistance) {
                    closestDistance = distance;
                    closestItem = item;
                  }
                });
                
                // Apply bounce animation only to the closest item
                closestItem.animate([
                  { transform: 'scale(1.0)' },
                  { transform: 'scale(1.15)' },
                  { transform: 'scale(1.0)' }
                ], {
                  duration: 300,
                  easing: 'ease-in-out'
                });
              } else {
                // ???
              }
            }, 50); // Short delay to ensure DOM is updated
          };
          
          // Apply the bounce effect after a short delay
          setTimeout(bounceItem, 100);
        } else {
          setIsSpinning(false);
        }
      };
    })(reward); // Pass the reward to the closure to capture it

    requestAnimationFrame(spin);
  };

  return {
    reelRef,
    isSpinning,
    finalItemIndex,
    selectedReward,
    startSpinning
  };
};
